import { useRef, useEffect, useState, type CSSProperties } from 'react';

// ---------------------------------------------------------------------------
// MarwoodXMark — Golden X SVG (from marwoodx-web)
// ---------------------------------------------------------------------------

interface MarwoodXMarkProps {
  gold?: boolean;
  color?: string;
  strokeWidth?: number;
  size?: number | string;
  style?: CSSProperties;
  className?: string;
}

function MarwoodXMark({
  gold = true,
  color,
  strokeWidth = 8,
  size,
  style,
  className,
}: MarwoodXMarkProps) {
  const useGold = gold && !color;
  const strokeColor = color ?? (useGold ? 'url(#xmark-gold)' : '#dc2626');

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      width={size}
      height={size}
      style={style}
      className={className}
      aria-hidden="true"
      fill="none"
    >
      {useGold && (
        <defs>
          <linearGradient id="xmark-gold" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffd700" />
            <stop offset="30%" stopColor="#ffaa22" />
            <stop offset="60%" stopColor="#dda030" />
            <stop offset="100%" stopColor="#cc8820" />
          </linearGradient>
        </defs>
      )}
      <path
        d="M18 12 L50 50 L82 12"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="square"
      />
      <path
        d="M18 88 L50 50 L82 88"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="square"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// MarwoodLogoAnimated — Golden X with CSS entrance animation
// ---------------------------------------------------------------------------

export interface MarwoodLogoAnimatedProps {
  size?: number;
  href?: string;
  className?: string;
  style?: CSSProperties;
}

export function MarwoodLogoAnimated({
  size = 56,
  href = 'https://marwood.au',
  className,
  style,
}: MarwoodLogoAnimatedProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '-40px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const content = (
    <div
      ref={ref}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1) rotate(0deg)' : 'scale(0.75) rotate(-15deg)',
        transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
        ...style,
      }}
    >
      <MarwoodXMark gold size={size} />
    </div>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Marwood X"
        style={{ display: 'inline-flex', textDecoration: 'none' }}
      >
        {content}
      </a>
    );
  }

  return content;
}

// ---------------------------------------------------------------------------
// MarwoodAttribution — "Built by" + logo
// ---------------------------------------------------------------------------

export interface MarwoodAttributionProps {
  href?: string;
  className?: string;
  style?: CSSProperties;
}

export function MarwoodAttribution({
  href = 'https://marwood.au',
  className,
  style,
}: MarwoodAttributionProps) {
  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', ...style }}
    >
      <span
        style={{
          fontSize: '0.75rem',
          color: '#737373',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        Built by
      </span>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Marwood X — Software. Strategy. Scale."
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          textDecoration: 'none',
          color: '#f5f5f5',
          fontSize: '0.8rem',
          fontWeight: 600,
        }}
      >
        Marwood
        <span
          style={{
            background: 'linear-gradient(135deg, #ffd700, #ffaa22, #dda030, #cc8820)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
          }}
        >
          X
        </span>
      </a>
    </span>
  );
}
